"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CloudRain, Check, Loader2 } from "lucide-react";

import { useRain } from "@/app/hooks/context/RainContext";
import RainModal from "@/app/components/Chat/RainModal";
import SokoAmount from "@/app/components/SokoAmount";

/** Falling drops behind the box while the join window is open. */
const DROPS = [8, 21, 34, 47, 60, 73, 86];

const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * The rain box above the chat feed: icon, pot, time left and a progress bar for
 * the cycle, with the two actions spelled out underneath — tipping your own
 * points in, and joining the split. Tapping the top half opens the full panel.
 *
 * Eligibility is summarised on the box as well, so a player knows where they
 * stand without opening anything.
 */
export default function RainBanner() {
    const { enabled, rain, result, secondsLeft, progress, loggedIn, joining, error, join } = useRain();
    const [modalOpen, setModalOpen] = useState(false);

    if (!enabled || !rain) return null;

    const { eligibility } = rain;
    const open = rain.status === "open";
    const paying = open && secondsLeft <= 0 && !result;

    const joinTitle = !loggedIn
        ? "Log in to join"
        : !eligibility.eligible
            ? `Play a game with Soko Points first — rounds in the last ${eligibility.days} days count`
            : !open
                ? "Joining opens shortly before the payout"
                : "Join this rain";

    return (
        <div className="px-3 pt-3">
            <AnimatePresence mode="wait">
                {result ? (
                    /* ---------- Just paid ---------- */
                    <motion.div
                        key="result"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="rounded-xl bg-gray-800 px-4 py-3"
                    >
                        <div className="flex items-center gap-2">
                            <CloudRain className="w-4 h-4 text-green-400 shrink-0" />
                            <span className="text-sm font-semibold text-green-300 inline-flex items-center gap-1.5">
                                {result.voided ? (
                                    "Nobody joined the rain"
                                ) : (
                                    <>Rain paid — <SokoAmount value={result.pot} size={14} /></>
                                )}
                            </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1 flex flex-wrap items-center gap-1">
                            {result.voided ? (
                                <>
                                    <SokoAmount value={result.pot} size={12} /> rolls into the next one.
                                </>
                            ) : (
                                <>
                                    <SokoAmount value={result.share} size={12} /> each to {result.participantCount}{" "}
                                    {result.participantCount === 1 ? "player" : "players"}.
                                </>
                            )}
                            {result.myShare > 0 && (
                                <span className="text-green-300 font-semibold inline-flex items-center gap-1">
                                    You got <SokoAmount value={result.myShare} size={12} />
                                </span>
                            )}
                        </p>
                    </motion.div>
                ) : (
                    /* ---------- The box ---------- */
                    <motion.div
                        key="box"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="relative overflow-hidden rounded-xl bg-gray-800 px-3.5 py-3 transition-all duration-200"
                    >
                        {/* Rain, only while the window is open */}
                        {open && (
                            <div className="absolute inset-0 pointer-events-none" aria-hidden>
                                {DROPS.map((left, i) => (
                                    <motion.span
                                        key={left}
                                        className="absolute top-0 w-px h-4 bg-gradient-to-b from-transparent to-green-300/70"
                                        style={{ left: `${left}%` }}
                                        initial={{ y: -16, opacity: 0 }}
                                        animate={{ y: 110, opacity: [0, 1, 0] }}
                                        transition={{
                                            duration: 0.9 + (i % 4) * 0.25,
                                            repeat: Infinity,
                                            delay: (i % 5) * 0.18,
                                            ease: "linear",
                                        }}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Everything above the buttons opens the full panel */}
                        <button
                            type="button"
                            onClick={() => setModalOpen(true)}
                            title="Rain details"
                            className="relative w-full flex items-center gap-3 text-left"
                        >
                            {/* Left: icon */}
                            <div
                                className={`shrink-0 w-11 h-11 rounded-xl bg-black/40 flex items-center justify-center ${
                                    open ? "text-green-300" : "text-green-400"
                                }`}
                            >
                                <motion.span
                                    animate={open ? { y: [0, -2, 0] } : {}}
                                    transition={{ duration: 1.4, repeat: Infinity }}
                                >
                                    <CloudRain className="w-5 h-5" />
                                </motion.span>
                            </div>

                            {/* Middle: title, pot, time left */}
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-green-400">
                                        Chat Rain
                                    </span>
                                    {rain.joined ? (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-px rounded text-[9px] font-bold uppercase bg-emerald-600 text-white">
                                            <Check className="w-2.5 h-2.5" /> In
                                        </span>
                                    ) : open ? (
                                        <span className="px-1.5 py-px rounded text-[9px] font-bold uppercase bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 text-white">
                                            Join open
                                        </span>
                                    ) : (
                                        <span
                                            className={`px-1.5 py-px rounded text-[9px] font-bold uppercase ${
                                                eligibility.eligible
                                                    ? "bg-green-400/10 text-green-400"
                                                    : "bg-amber-400/10 text-amber-400"
                                            }`}
                                            title={
                                                loggedIn
                                                    ? `Joining needs a round played in the last ${eligibility.days} days`
                                                    : "Log in and play to join"
                                            }
                                        >
                                            {eligibility.eligible ? "Eligible" : "Not eligible"}
                                        </span>
                                    )}
                                </div>

                                <p className="text-xl font-bold text-white leading-tight mt-0.5">
                                    <SokoAmount value={rain.pot} size={16} />
                                </p>

                                <p className="text-[11px] text-gray-500 tabular-nums">
                                    {paying ? "paying out…" : `${formatCountdown(secondsLeft)} left`}
                                </p>
                            </div>

                            {/* Right: cycle progress */}
                            <div className="shrink-0 w-20 sm:w-24">
                                <div className="h-1.5 rounded-full bg-black/40 overflow-hidden">
                                    <motion.div
                                        className={`h-full rounded-full ${open ? "bg-emerald-400" : "bg-gray-600"}`}
                                        animate={{ width: `${Math.round(progress * 100)}%` }}
                                        transition={{ ease: "linear", duration: 0.4 }}
                                    />
                                </div>
                            </div>
                        </button>

                        {/* Actions — the two things a player can actually do */}
                        <div className="relative mt-3 flex gap-2">
                            <button
                                type="button"
                                onClick={() => setModalOpen(true)}
                                className="flex-1 py-2 rounded-xl bg-gray-800 border border-gray-700 text-[11px] font-semibold uppercase tracking-wide text-gray-200 hover:bg-gray-700 transition-colors duration-200"
                            >
                                Tip the rain
                            </button>

                            {rain.joined ? (
                                <div className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gray-800 border border-gray-700 text-[11px] font-semibold uppercase tracking-wide text-green-400">
                                    <Check className="w-3.5 h-3.5" /> You&apos;re in
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={join}
                                    disabled={!open || !loggedIn || !eligibility.eligible || joining || paying}
                                    title={joinTitle}
                                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 text-white text-[11px] font-bold uppercase tracking-wide hover:shadow-lg hover:shadow-emerald-500/20 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
                                >
                                    {joining && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                    Join the rain
                                </button>
                            )}
                        </div>

                        {error && <p className="relative mt-1.5 text-[11px] font-mono text-red-400">{error}</p>}
                    </motion.div>
                )}
            </AnimatePresence>

            {modalOpen && <RainModal onClose={() => setModalOpen(false)} />}
        </div>
    );
}
